import { isAnalyticsEnabled } from "./isAnalyticsEnabled";

/**
 * Umami analytics helpers — safe when script blocked or SSR.
 */

const STORAGE_KEY = "testsphere_umami_session_id";

/**
 * Origin + pathname only (drops query/hash) so tokens are not sent in event payloads.
 * @param {string} raw
 * @returns {string}
 */
export function sanitizeUrlForAnalytics(raw) {
  if (!raw || typeof raw !== "string") return "";
  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    const u = new URL(raw, base);
    return `${u.origin}${u.pathname}`;
  } catch {
    return raw.length > 200 ? `${raw.slice(0, 200)}...` : raw;
  }
}

function generateSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * @returns {string|undefined}
 */
export function getSessionId() {
  if (typeof window === "undefined" || !isAnalyticsEnabled()) return undefined;
  try {
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = generateSessionId();
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

/**
 * @param {string} eventName - dot notation e.g. api.call, ui.click
 * @param {Record<string, unknown>} [data]
 */
export function trackEvent(eventName, data = {}) {
  if (typeof window === "undefined" || !isAnalyticsEnabled()) return;
  const sessionId = getSessionId();
  const payload = {
    app: "testsphere",
    timestamp: new Date().toISOString(),
    ...(sessionId ? { session_id: sessionId } : {}),
    ...data,
  };
  const umami = window.umami;
  if (umami && typeof umami.track === "function") {
    try {
      umami.track(eventName, payload);
    } catch {
      /* no-op */
    }
  }
}
